// src/app/shared/services/call.service.ts

import {
  Injectable,
  inject,
  PLATFORM_ID,
  OnDestroy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';

import { SocketService }   from './socket.service';
import { AuthStateService } from '../states/auth.state';
import { IConversation, IUserSnippet, CallType } from '../models/messaging.models';

// ─── Types ────────────────────────────────────────────────────────────────────

export type CallState =
  | 'idle'
  | 'calling'     // outgoing — waiting for answer
  | 'ringing'     // incoming — not yet answered
  | 'connected'   // media flowing
  | 'ended';

export interface ICallSession {
  conversationId: string;
  callType:       CallType;
  remoteUser:     IUserSnippet;
  state:          CallState;
  startedAt?:     Date;
  isMuted:        boolean;
  isCameraOff:    boolean;
  /** True once the callee's client has confirmed it received the incoming
   *  call and is actively ringing — lets the caller's UI move from
   *  "Calling…" to "Ringing…" the way a phone does. Only meaningful while
   *  state === 'calling'. */
  remoteRinging:  boolean;
}

// ─── STUN config ──────────────────────────────────────────────────────────────

const RTC_CONFIG: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class CallService implements OnDestroy {

  private platformId = inject(PLATFORM_ID);
  private socket     = inject(SocketService);
  private authState  = inject(AuthStateService);

  // ── Public state ──────────────────────────────────────────────────────────
  readonly callSession$  = new BehaviorSubject<ICallSession | null>(null);
  readonly localStream$  = new BehaviorSubject<MediaStream | null>(null);
  readonly remoteStream$ = new BehaviorSubject<MediaStream | null>(null);
  readonly callError$    = new Subject<string>();
  /** Emits once when a connected call ends — carries the final duration string and call type */
  readonly callEnded$    = new Subject<{ duration: string; callType: CallType }>();

  // ── Internal ──────────────────────────────────────────────────────────────
  private pc:          RTCPeerConnection | null = null;
  private subs         = new Subscription();
  private callTimer:   ReturnType<typeof setInterval> | null = null;
  private callSeconds  = 0;

  // BUG FIX 3: Queue ICE candidates that arrive before remote description is set
  private iceCandidateQueue: RTCIceCandidateInit[] = [];
  private remoteDescSet      = false;

  // Pending offer held until answerCall() is invoked
  private pendingOffer: RTCSessionDescriptionInit | null = null;

  // ── Ringtone / ring-back engine ─────────────────────────────────────────
  private audioCtx:        AudioContext | null = null;
  private ringLoopHandle:  ReturnType<typeof setInterval> | null = null;
  private ringToneState:   'none' | 'ringback' | 'incoming' = 'none';

  // ═══════════════════════════════════════════════════════════════════════════
  // INIT  — BUG FIX 1: initSignalling() must be called from ChatService.init()
  // ═══════════════════════════════════════════════════════════════════════════

  private signallingInitialised = false;

  initSignalling(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Guard: never register listeners twice on the same service instance
    if (this.signallingInitialised) return;
    this.signallingInitialised = true;
    this._registerSocketListeners();
    this._registerUnloadHandler();
    this._registerRingtoneEngine();
    console.log('[CallService] Signalling listeners registered');
  }

  ngOnDestroy(): void {
    this._cleanup();
    this.subs.unsubscribe();
    this.audioCtx?.close().catch(() => {});
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC — CALL CONTROL
  // ═══════════════════════════════════════════════════════════════════════════

  async initiateCall(conversation: IConversation, callType: CallType): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.callSession$.value) {
      console.warn('[CallService] Already in a call');
      return;
    }

    const remoteUser = this._getRemoteUser(conversation);
    if (!remoteUser) {
      console.error('[CallService] Could not identify remote user — check participants array');
      return;
    }

    this._setSession({
      conversationId: conversation._id,
      callType,
      remoteUser,
      state: 'calling',
      isMuted: false,
      isCameraOff: false,
      remoteRinging: false,
    });

    try {
      const stream = await this._getLocalMedia(callType);
      this.localStream$.next(stream);

      this.pc = this._createPeerConnection(conversation._id);
      stream.getTracks().forEach(track => this.pc!.addTrack(track, stream));

      const offer = await this.pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: callType === 'VIDEO' });
      await this.pc.setLocalDescription(offer);

      this.socket.emit('call:initiate', { conversationId: conversation._id, callType, offer });

      console.log('[CallService] Offer sent for conversation', conversation._id);
    } catch (err: any) {
      console.error('[CallService] initiateCall error:', err);
      this.callError$.next(this._mediaErrorMessage(err, 'start'));
      this._cleanup();
    }
  }

  async answerCall(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    const session = this.callSession$.value;
    if (!session || session.state !== 'ringing' || !this.pendingOffer) {
      console.warn('[CallService] answerCall called in wrong state', session?.state);
      return;
    }

    try {
      const stream = await this._getLocalMedia(session.callType);
      this.localStream$.next(stream);

      this.pc = this._createPeerConnection(session.conversationId);
      stream.getTracks().forEach(track => this.pc!.addTrack(track, stream));

      // Set remote description (offer) then drain queued ICE candidates
      await this.pc.setRemoteDescription(new RTCSessionDescription(this.pendingOffer));
      this.remoteDescSet = true;
      await this._drainIceCandidateQueue();

      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);

      this.socket.emit('call:answer', { conversationId: session.conversationId, answer });
      // Optimistic local start — corrected shortly after by the authoritative
      // 'call:started' timestamp from the server so both sides' timers agree.
      this._setSession({ ...session, state: 'connected', startedAt: new Date() });
      this._startCallTimer();
      this.pendingOffer = null;

      console.log('[CallService] Call answered');
    } catch (err: any) {
      console.error('[CallService] answerCall error:', err);
      this.callError$.next(this._mediaErrorMessage(err, 'answer'));

      // BUG FIX: previously this only cleaned up locally, so if the callee's
      // getUserMedia failed (e.g. mediaDevices unavailable), the caller was
      // never told — their side just sat on "Calling…"/"Ringing…" forever
      // with a dangling peer connection, since call:answer was never sent.
      // Tell the server the same way an explicit decline would, so the
      // caller's overlay clears via the existing call:rejected handler.
      const session = this.callSession$.value;
      if (session) {
        this.socket.emit('call:reject', { conversationId: session.conversationId });
      }
      this._cleanup();
    }
  }

  rejectCall(): void {
    const session = this.callSession$.value;
    if (!session) return;
    this.socket.emit('call:reject', { conversationId: session.conversationId });
    this._cleanup();
  }

  cancelCall(): void {
    const session = this.callSession$.value;
    if (!session) return;
    this.socket.emit('call:cancel', { conversationId: session.conversationId });
    this._cleanup();
  }

  endCall(): void {
    const session = this.callSession$.value;
    if (!session) return;
    this.socket.emit('call:end', { conversationId: session.conversationId, duration: this.callSeconds });
    this._emitCallEndedToast(session.callType, this.callSeconds);
    this._cleanup();
  }

  toggleMute(): void {
    const session = this.callSession$.value;
    if (!session) return;
    this.localStream$.value?.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
    this._setSession({ ...session, isMuted: !session.isMuted });
  }

  toggleCamera(): void {
    const session = this.callSession$.value;
    if (!session) return;
    this.localStream$.value?.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
    this._setSession({ ...session, isCameraOff: !session.isCameraOff });
  }

  /**
   * Refreshing or closing the tab mid-call previously left the OTHER
   * participant's UI connected/ringing for up to 8s (the disconnect grace
   * period meant for hot-reload/brief network drops), and left a stale
   * entry on the backend that could block the next call attempt on this
   * conversation. Proactively signal a hangup the moment the page is about
   * to unload so both sides end immediately.
   */
  private _registerUnloadHandler(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const handler = () => this._hangupOnUnload();
    window.addEventListener('pagehide', handler);
    window.addEventListener('beforeunload', handler);
  }

  private _hangupOnUnload(): void {
    const session = this.callSession$.value;
    if (!session) return;

    switch (session.state) {
      case 'connected':
        this.socket.emit('call:end', { conversationId: session.conversationId, duration: this.callSeconds });
        break;
      case 'calling':
        this.socket.emit('call:cancel', { conversationId: session.conversationId });
        break;
      case 'ringing':
        this.socket.emit('call:reject', { conversationId: session.conversationId });
        break;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE — Socket listeners
  // ═══════════════════════════════════════════════════════════════════════════

  private _registerSocketListeners(): void {

    // ── Incoming call ──────────────────────────────────────────────────────
    this.subs.add(
      this.socket.on<{
        conversationId: string;
        callType:       CallType;
        callerId:       string;
        callerInfo:     IUserSnippet;
        offer:          RTCSessionDescriptionInit;
      }>('call:incoming').subscribe(payload => {
        console.log('[CallService] call:incoming received', payload.conversationId);

        if (this.callSession$.value) {
          console.log('[CallService] Already in call — sending busy');
          this.socket.emit('call:busy', { conversationId: payload.conversationId });
          return;
        }

        this.pendingOffer  = payload.offer;
        this.remoteDescSet = false;
        this.iceCandidateQueue = [];

        this._setSession({
          conversationId: payload.conversationId,
          callType:       payload.callType,
          remoteUser:     payload.callerInfo,
          state:          'ringing',
          isMuted:        false,
          isCameraOff:    false,
          remoteRinging:  false,
        });

        // Tell the caller that this device actually received the call and
        // is ringing — without this, the caller's UI has no way to know the
        // difference between "still trying to reach them" and "reached them,
        // phone is ringing now", so it just says "Calling…" the whole time.
        this.socket.emit('call:ringing', { conversationId: payload.conversationId });
      }),
    );

    // ── Callee confirmed they're ringing — flip "Calling…" to "Ringing…" ──
    this.subs.add(
      this.socket.on<{ conversationId: string }>('call:ringing')
        .subscribe(payload => {
          const session = this.callSession$.value;
          if (!session || session.conversationId !== payload.conversationId) return;
          if (session.state !== 'calling' || session.remoteRinging) return;
          this._setSession({ ...session, remoteRinging: true });
        }),
    );

    // ── Call answered — caller receives callee's answer SDP ───────────────
    this.subs.add(
      this.socket.on<{
        conversationId: string;
        answer:         RTCSessionDescriptionInit;
        startedAt?:     string;
      }>('call:answered').subscribe(async payload => {
        console.log('[CallService] call:answered received');

        if (!this.pc) {
          console.warn('[CallService] No peer connection when answer arrived');
          return;
        }

        try {
          await this.pc.setRemoteDescription(new RTCSessionDescription(payload.answer));
          this.remoteDescSet = true;
          await this._drainIceCandidateQueue();

          const session = this.callSession$.value;
          if (session) {
            this._setSession({
              ...session,
              state: 'connected',
              startedAt: payload.startedAt ? new Date(payload.startedAt) : new Date(),
            });
            this._startCallTimer();
          }
        } catch (err) {
          console.error('[CallService] setRemoteDescription (answer) error:', err);
        }
      }),
    );

    // ── Call started — authoritative startedAt from the server, sent to
    //    both participants so their on-screen call timers stay in sync
    //    instead of drifting apart based on each client's own clock. ────────
    this.subs.add(
      this.socket.on<{ conversationId: string; startedAt: string }>('call:started')
        .subscribe(payload => {
          const session = this.callSession$.value;
          if (!session || session.conversationId !== payload.conversationId) return;
          this._setSession({ ...session, state: 'connected', startedAt: new Date(payload.startedAt) });
        }),
    );

    // ── Server-side errors for call events — e.g. a rejected call:initiate
    //    (stale/active call, missing participants, etc.). Without this the
    //    caller's UI just hung on "Calling…" forever with no feedback,
    //    which looked like the call had silently "not registered". ────────
    this.subs.add(
      this.socket.on<{ event: string; message: string }>('error').subscribe(err => {
        if (!err?.event?.startsWith('call:')) return;
        console.error('[CallService] Server rejected call event:', err.event, err.message);
        this.callError$.next(err.message || 'Call failed — please try again');
        if (this.callSession$.value) {
          this._cleanup();
        }
      }),
    );

    // ── ICE candidate — BUG FIX 3: queue until remote desc is set ─────────
    this.subs.add(
      this.socket.on<{
        conversationId: string;
        candidate:      RTCIceCandidateInit;
      }>('call:ice').subscribe(async payload => {
        if (!payload.candidate) return;

        if (this.remoteDescSet && this.pc) {
          try {
            await this.pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
          } catch (err) {
            console.warn('[CallService] addIceCandidate error:', err);
          }
        } else {
          // Queue for later — remote desc not yet set
          console.log('[CallService] Queuing ICE candidate');
          this.iceCandidateQueue.push(payload.candidate);
        }
      }),
    );

    // ── Remote rejected ────────────────────────────────────────────────────
    this.subs.add(
      this.socket.on<{ conversationId: string; calleeId: string }>('call:rejected')
        .subscribe(() => {
          console.log('[CallService] Call rejected');
          this.callError$.next('Call declined');
          this._cleanup();
        }),
    );

    // ── Caller cancelled before answer ────────────────────────────────────
    this.subs.add(
      this.socket.on<{ conversationId: string }>('call:cancelled')
        .subscribe(() => {
          console.log('[CallService] Call cancelled by caller');
          this._cleanup();
        }),
    );

    // ── Call ended by remote ───────────────────────────────────────────────
    this.subs.add(
      this.socket.on<{
        conversationId: string;
        endedBy:        string;
        duration?:      number | null;
      }>('call:ended').subscribe(payload => {
        console.log('[CallService] Call ended by remote, duration:', payload.duration);
        const session = this.callSession$.value;
        if (session && session.state === 'connected') {
          this._emitCallEndedToast(session.callType, payload.duration ?? this.callSeconds);
        }
        this._cleanup();
      }),
    );

    // ── Remote is busy ─────────────────────────────────────────────────────
    this.subs.add(
      this.socket.on<{ conversationId: string }>('call:busy')
        .subscribe(() => {
          console.log('[CallService] Remote is busy');
          this.callError$.next('User is busy');
          this._cleanup();
        }),
    );

    // ── Missed call timeout ────────────────────────────────────────────────
    this.subs.add(
      this.socket.on<{ conversationId: string; callerId: string }>('call:missed')
        .subscribe(() => {
          console.log('[CallService] Call missed (no answer)');
          this._cleanup();
        }),
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE — WebRTC helpers
  // ═══════════════════════════════════════════════════════════════════════════

  private _createPeerConnection(conversationId: string): RTCPeerConnection {
    const pc = new RTCPeerConnection(RTC_CONFIG);

    // Trickle ICE — send candidates as they are discovered
    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        this.socket.emit('call:ice', { conversationId, candidate: candidate.toJSON() });
      }
    };

    pc.onicegatheringstatechange = () => {
      console.log('[CallService] ICE gathering state:', pc.iceGatheringState);
    };

    pc.oniceconnectionstatechange = () => {
      console.log('[CallService] ICE connection state:', pc.iceConnectionState);
    };

    pc.onconnectionstatechange = () => {
      console.log('[CallService] Connection state:', pc.connectionState);
      if (pc.connectionState === 'failed') {
        console.error('[CallService] Peer connection failed');
        this.callError$.next('Connection failed — check your network');
        this._cleanup();
      }
    };

    // BUG FIX 4: Use ontrack properly — build remote stream from all incoming tracks
    const remoteStream = new MediaStream();
    this.remoteStream$.next(remoteStream);

    pc.ontrack = (event) => {
      console.log('[CallService] Remote track received:', event.track.kind);
      // Prefer the stream from the event if available
      if (event.streams && event.streams[0]) {
        this.remoteStream$.next(event.streams[0]);
      } else {
        remoteStream.addTrack(event.track);
        this.remoteStream$.next(remoteStream);
      }
    };

    return pc;
  }

  private async _getLocalMedia(callType: CallType): Promise<MediaStream> {
    console.log('[CallService] Requesting media:', callType);

    // `navigator.mediaDevices` itself (not just getUserMedia) is undefined
    // on insecure origins (http:// on anything other than localhost) and in
    // some embedded WebViews that haven't declared camera/mic permissions.
    // Calling straight into it throws a plain TypeError with no .name, so
    // the NotAllowedError branch below never matches and the user gets a
    // useless "Failed to start/answer call" message. Fail fast with a
    // typed, recognisable error instead — same guard already used in
    // camera-capture.ts / video-capture.ts.
    if (!navigator.mediaDevices?.getUserMedia) {
      const err = new Error(
        'Camera/microphone API unavailable — the page must be served over HTTPS (or localhost).',
      );
      err.name = 'MediaUnavailableError';
      throw err;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
        video: callType === 'VIDEO' ? { width: { ideal: 1280 }, height: { ideal: 720 } } : false,
      });
      console.log('[CallService] Got local media, tracks:', stream.getTracks().map(t => t.kind));
      return stream;
    } catch (err: any) {
      console.error('[CallService] getUserMedia error:', err.name, err.message);
      throw err;
    }
  }

  /** Human-readable message for a failed _getLocalMedia() call. */
  private _mediaErrorMessage(err: any, action: 'start' | 'answer'): string {
    if (err?.name === 'NotAllowedError') return 'Camera/microphone permission denied';
    if (err?.name === 'MediaUnavailableError') return err.message;
    return `Failed to ${action} call`;
  }

  /** Apply all queued ICE candidates after remote description is set */
  private async _drainIceCandidateQueue(): Promise<void> {
    console.log('[CallService] Draining ICE queue, size:', this.iceCandidateQueue.length);
    while (this.iceCandidateQueue.length > 0) {
      const candidate = this.iceCandidateQueue.shift()!;
      try {
        await this.pc?.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.warn('[CallService] Queued ICE candidate error:', err);
      }
    }
  }

  private _startCallTimer(): void {
    this.callSeconds = 0;
    if (this.callTimer) clearInterval(this.callTimer);
    this.callTimer = setInterval(() => { this.callSeconds++; }, 1_000);
  }

  private _setSession(session: ICallSession): void {
    this.callSession$.next(session);
  }

  /** Format seconds as "m:ss" and emit callEnded$ (only for answered calls) */
  private _emitCallEndedToast(callType: CallType, seconds: number): void {
    if (seconds <= 0) return;   // don't show toast for unanswered / 0-second calls
    const m = Math.floor(seconds / 60);
    const s = (seconds % 60).toString().padStart(2, '0');
    const duration = m > 0 ? `${m}:${s}` : `0:${s}`;
    this.callEnded$.next({ duration, callType });
  }

  private _cleanup(): void {
    if (this.callTimer) { clearInterval(this.callTimer); this.callTimer = null; }
    this._stopRingtone();

    this.localStream$.value?.getTracks().forEach(t => t.stop());
    this.localStream$.next(null);

    this.remoteStream$.value?.getTracks().forEach(t => t.stop());
    this.remoteStream$.next(null);

    this.pc?.close();
    this.pc = null;

    this.pendingOffer      = null;
    this.remoteDescSet     = false;
    this.iceCandidateQueue = [];
    this.callSeconds       = 0;

    this.callSession$.next(null);
    console.log('[CallService] Cleaned up');
  }

  private _getRemoteUser(conv: IConversation): IUserSnippet | null {
    const me = this.authState.getAuthSnapshot().user;
    const myId = me?.id ?? me?._id;
    if (!myId) {
      console.warn('[CallService] myId is undefined — user not in auth state');
      return null;
    }
    const remote = conv.participants.find(p => p._id !== myId);
    if (!remote) console.warn('[CallService] Could not find remote participant. myId=', myId, 'participants=', conv.participants.map(p => p._id));
    return remote ?? null;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE — Ringtone / ring-back engine
  //
  // Neither side ever heard a ring — there was no ringtone implementation at
  // all. Rather than depend on a bundled audio file (which the app may not
  // ship), tones are synthesised with the Web Audio API: a classic dual-tone
  // ring-back pattern for the caller ("Calling…"/"Ringing…") and a distinct
  // pulsed tone for the callee's incoming-call banner.
  // ═══════════════════════════════════════════════════════════════════════════

  /** Starts/stops the right tone automatically as the call session changes state */
  private _registerRingtoneEngine(): void {
    // Any real interaction anywhere on the page unlocks the AudioContext for
    // the rest of the session — browsers block audio (even synthesised)
    // from starting with zero prior user gesture. This is a one-time,
    // page-wide safety net so an incoming call's ringtone can actually play
    // even if the very first thing the user does is receive a call.
    const unlock = () => { this._ensureAudioCtx(); };
    window.addEventListener('pointerdown', unlock, { once: true, passive: true });
    window.addEventListener('keydown',     unlock, { once: true });

    this.subs.add(
      this.callSession$.subscribe(session => {
        if (!session) { this._stopRingtone(); return; }

        if (session.state === 'calling') {
          this._startRingback();
        } else if (session.state === 'ringing') {
          this._startIncomingRing();
        } else {
          this._stopRingtone();
        }
      }),
    );
  }

  private _ensureAudioCtx(): AudioContext | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    const Ctor = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!this.audioCtx) this.audioCtx = new Ctor();
    if (this.audioCtx.state === 'suspended') this.audioCtx.resume().catch(() => {});
    return this.audioCtx;
  }

  /** Plays a short tone (one or more simultaneous frequencies) for durationMs */
  private _playTonePulse(freqs: number[], durationMs: number, volume = 0.15): void {
    const ctx = this._ensureAudioCtx();
    if (!ctx) return;

    const gain = ctx.createGain();
    gain.gain.value = volume;
    gain.connect(ctx.destination);

    const oscillators = freqs.map(freq => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      osc.start();
      return osc;
    });

    setTimeout(() => {
      oscillators.forEach(osc => {
        try { osc.stop(); osc.disconnect(); } catch { /* already stopped */ }
      });
      gain.disconnect();
    }, durationMs);
  }

  /** Outgoing ring-back — the tone the CALLER hears while waiting (classic 2s-on/4s-off) */
  private _startRingback(): void {
    if (this.ringToneState === 'ringback') return;
    this._stopRingtone();
    this.ringToneState = 'ringback';

    const cycle = () => this._playTonePulse([440, 480], 2000, 0.1);
    cycle();
    this.ringLoopHandle = setInterval(cycle, 6000);
  }

  /** Incoming ringtone — the tone the CALLEE hears on their ringing banner */
  private _startIncomingRing(): void {
    if (this.ringToneState === 'incoming') return;
    this._stopRingtone();
    this.ringToneState = 'incoming';

    const cycle = () => {
      this._playTonePulse([880], 350, 0.18);
      setTimeout(() => this._playTonePulse([880], 350, 0.18), 500);
    };
    cycle();
    this.ringLoopHandle = setInterval(cycle, 2000);
  }

  private _stopRingtone(): void {
    if (this.ringLoopHandle) { clearInterval(this.ringLoopHandle); this.ringLoopHandle = null; }
    this.ringToneState = 'none';
  }
}